import { Popover } from '@/components/ui/Popover';
import { getEventDisplayStatus, getEventStatusLabel } from '@/lib/eventHelpers';
import { formatEventDate } from '@/lib/utils';
import type { Event } from '@eventflow/shared-types';
import {
  Archive,
  BarChart3,
  Calendar,
  CheckCircle2,
  Clock,
  Edit,
  Eye,
  Map as MapIcon,
  MapPin,
  MoreVertical,
  Pause,
  Play,
  RefreshCw,
  Target,
  Theater,
  Trash2,
  Users,
} from 'lucide-react';
import { memo, useState } from 'react';
import { Link } from 'react-router-dom';

type EventSalesInfo = {
  total_seats_count?: number;
  sold_seats_count?: number;
  seats?: Array<{ count: number }>;
};

interface EventCardProps {
  event: Event & EventSalesInfo;
  onTogglePublish: (id: string, currentStatus: boolean, name: string) => void;
  onDelete: (id: string, name: string) => void;
  onFinish?: (id: string, name: string) => void;
  onRepublish?: (id: string, name: string) => void;
  onArchive?: (id: string, name: string) => void;
  onManageTeam?: (id: string, name: string) => void;
}

// Memoizar para evitar re-renders innecesarios en listas de eventos
// Cuando hay múltiples EventCards y uno cambia, evita re-renderizar los demás
export const EventCard = memo(function EventCard({
  event,
  onTogglePublish,
  onDelete,
  onFinish,
  onRepublish,
  onArchive,
  onManageTeam,
}: EventCardProps) {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const totalSeats = event.total_seats_count ?? event.seats?.[0]?.count ?? 0;
  const soldSeats = event.sold_seats_count ?? 0;
  const hiddenSeats = Array.isArray(event.layout_data?.shapes)
    ? event.layout_data!.shapes
      .filter((shape: any) => shape?.hidden === true && shape?.category !== 'overlay')
      .reduce((sum: number, shape: any) => {
        const capacity = typeof shape?.capacity === 'number' ? shape.capacity : 0;
        return sum + (capacity > 0 ? capacity : 1);
      }, 0)
    : 0;
  const visibleTotalSeats = Math.max(totalSeats - hiddenSeats, 0);
  const soldPercentage =
    visibleTotalSeats > 0 ? Math.min((soldSeats / visibleTotalSeats) * 100, 100) : 0;
  const badgeStatus = getEventDisplayStatus({
    status: event.status,
    event_date: event.event_date,
    event_end_time: event.event_end_time ?? null,
  });

  return (
    <div className="group flex flex-col h-full bg-white border border-slate-200 rounded-xl shadow-sm hover:shadow-md transition-all duration-200 overflow-hidden">
      {/* ZONA SUPERIOR: Media & Badge */}
      <div className="relative aspect-video w-full bg-slate-100 overflow-hidden border-b border-slate-100">
        {event.venue_image_url ? (
          <img
            src={event.venue_image_url}
            alt={event.venue_name}
            className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
            loading="lazy"
            decoding="async"
          />
        ) : (
          <div className="w-full h-full bg-slate-100 flex items-center justify-center">
            <Theater className="w-12 h-12 text-slate-300" />
          </div>
        )}

        {/* Badge de Estado Flotante */}
        <div className="absolute top-3 right-3">
          <span
            className={`
              px-2.5 py-1 rounded-full text-xs font-semibold shadow-sm backdrop-blur-md
              ${badgeStatus === 'archived'
                ? 'bg-gray-600/90 text-white border border-gray-500/50'
                : badgeStatus === 'finished'
                  ? 'bg-slate-500/90 text-white border border-slate-400/50'
                  : badgeStatus === 'published'
                    ? 'bg-emerald-500/90 text-white border border-emerald-400/50'
                    : 'bg-amber-400/90 text-amber-950 border border-amber-300/50'
              }
            `}
          >
            {getEventStatusLabel(badgeStatus)}
          </span>
        </div>
      </div>

      {/* ZONA MEDIA: Contenido */}
      <div className="flex-1 p-5 flex flex-col gap-4">
        {/* Título */}
        <div>
          <h3
            className="text-lg font-semibold text-slate-900 truncate leading-tight"
            title={event.name}
          >
            {event.name}
          </h3>
        </div>

        {/* Meta Info */}
        <div className="space-y-2.5">
          {/* Fecha */}
          <div className="flex items-center gap-2.5 text-sm text-gray-700">
            <Calendar className="w-4 h-4 shrink-0 text-blue-600" />
            <span className="truncate capitalize font-semibold">{formatEventDate(event.event_date)}</span>
          </div>

          {/* Horario y Duración */}
          {event.event_start_time && event.event_end_time && (
            <div className="flex items-center gap-2.5 text-sm text-gray-700">
              <Clock className="w-4 h-4 shrink-0 text-blue-600" />
              <div className="flex gap-1 truncate">
                <span className="font-semibold">{event.event_start_time.slice(0, 5)} - {event.event_end_time.slice(0, 5)}</span>
                {(() => {
                  const [startH, startM] = event.event_start_time!.split(':').map(Number);
                  const [endH, endM] = event.event_end_time!.split(':').map(Number);
                  const diff = (endH * 60 + endM) - (startH * 60 + startM);
                  if (diff > 0) {
                    const h = Math.floor(diff / 60);
                    const m = diff % 60;
                    return <span className="text-gray-500 font-normal">({h}h {m}min)</span>;
                  }
                  return null;
                })()}
              </div>
            </div>
          )}

          {/* Lugar */}
          <div className="flex items-center gap-2.5 text-sm text-gray-700">
            <MapPin className="w-4 h-4 shrink-0 text-blue-600" />
            <span className="truncate font-semibold">{event.venue_name}</span>
          </div>

          {/* Organizadores (si aplica) */}
          {event.organizers && event.organizers.length > 1 && (
            <div className="flex items-center gap-2.5 text-sm text-gray-700">
              <Users className="w-4 h-4 shrink-0 text-blue-600" />
              <span>{event.organizers.length} organizadores</span>
            </div>
          )}
        </div>

        {/* Resumen de Ventas (Placeholder Visual) */}
        <div className="mt-auto pt-2">
          <div className="flex items-center justify-between text-xs text-slate-500 mb-1.5">
            <span>Ventas</span>
            <span className="font-medium text-slate-700">
              {soldSeats} / {visibleTotalSeats}
              <span className="text-slate-500"> ({soldPercentage.toFixed(1)}%)</span>
            </span>
          </div>
          <div className="h-1.5 w-full bg-slate-100 rounded-full overflow-hidden">
            <div
              className="h-full bg-blue-500 rounded-full transition-all duration-300"
              style={{ width: `${soldPercentage}%` }}
            />
          </div>
        </div>
      </div>

      {/* ZONA INFERIOR: Acciones */}
      <div className="p-4 bg-slate-50/50 border-t border-slate-100 flex items-center gap-3">
        {/* Botón Principal */}
        <Link
          to={`/dashboard/events/${event.id}`}
          className="flex-1 inline-flex items-center justify-center gap-2 bg-white border border-slate-200 hover:border-blue-300 hover:text-blue-600 text-slate-700 px-4 py-2 rounded-lg text-sm font-medium transition-colors shadow-sm"
        >
          <Target className="w-4 h-4" />
          Gestionar
        </Link>

        {/* Menú de Acciones Secundarias (Popover) */}
        <Popover
          open={isMenuOpen}
          onOpenChange={setIsMenuOpen}
          align="end"
          width={200}
          trigger={
            <button
              className={`p-2 rounded-lg transition-colors ${isMenuOpen ? 'bg-slate-200 text-slate-700' : 'text-slate-400 hover:text-slate-600 hover:bg-slate-100'}`}
            >
              <MoreVertical className="w-5 h-5" />
            </button>
          }
        >
          <div className="p-1 flex flex-col gap-1">
            <Link
              to={`/dashboard/events/${event.id}/map`}
              className="flex items-center gap-2 px-3 py-2 text-sm text-slate-600 hover:bg-slate-50 hover:text-slate-900 rounded-md"
            >
              <MapIcon className="w-4 h-4" /> Editor de Mapa
            </Link>
            <Link
              to={`/eventos/${event.id}`}
              className="flex items-center gap-2 px-3 py-2 text-sm text-slate-600 hover:bg-slate-50 hover:text-slate-900 rounded-md"
            >
              <Eye className="w-4 h-4" /> Ver Evento
            </Link>
            <Link
              to={`/dashboard/events/${event.id}/analytics`}
              className="flex items-center gap-2 px-3 py-2 text-sm text-slate-600 hover:bg-slate-50 hover:text-slate-900 rounded-md"
            >
              <BarChart3 className="w-4 h-4" /> Estadísticas
            </Link>
            <Link
              to={`/dashboard/events/${event.id}/edit`}
              className="flex items-center gap-2 px-3 py-2 text-sm text-slate-600 hover:bg-slate-50 hover:text-slate-900 rounded-md"
            >
              <Edit className="w-4 h-4" /> Editar Detalles
            </Link>

            {/* Gestión de Equipo */}
            <button
              onClick={() => {
                onManageTeam?.(event.id, event.name);
                setIsMenuOpen(false);
              }}
              className="flex items-center gap-2 px-3 py-2 text-sm text-slate-600 hover:bg-slate-50 hover:text-slate-900 rounded-md w-full text-left"
            >
              <Users className="w-4 h-4" /> Gestionar Equipo
            </button>

            <div className="h-px bg-slate-100 my-1" />

            {/* ESTADO: PUBLICADO - Mostrar "Pausar Evento" y "Finalizar Evento" */}
            {badgeStatus === 'published' && (
              <>
                <button
                  onClick={() => {
                    onTogglePublish(event.id, event.is_published, event.name);
                    setIsMenuOpen(false);
                  }}
                  className="flex items-center gap-2 px-3 py-2 text-sm text-amber-600 hover:bg-amber-50 rounded-md w-full text-left"
                >
                  <Pause className="w-4 h-4" /> Pausar Evento
                </button>
                {onFinish && (
                  <button
                    onClick={() => {
                      onFinish(event.id, event.name);
                      setIsMenuOpen(false);
                    }}
                    className="flex items-center gap-2 px-3 py-2 text-sm text-blue-600 hover:bg-blue-50 rounded-md w-full text-left"
                  >
                    <CheckCircle2 className="w-4 h-4" /> Finalizar Evento
                  </button>
                )}
              </>
            )}

            {/* ESTADO: BORRADOR - Mostrar "Reanudar"/"Publicar Evento" y "Finalizar Evento" */}
            {badgeStatus === 'draft' && (
              <>
                <button
                  onClick={() => {
                    onTogglePublish(event.id, event.is_published, event.name);
                    setIsMenuOpen(false);
                  }}
                  className="flex items-center gap-2 px-3 py-2 text-sm text-emerald-600 hover:bg-emerald-50 rounded-md w-full text-left"
                >
                  <Play className="w-4 h-4" />
                  {event.is_published === false && soldSeats > 0 ? 'Reanudar Evento' : 'Publicar Evento'}
                </button>
                {onFinish && (
                  <button
                    onClick={() => {
                      onFinish(event.id, event.name);
                      setIsMenuOpen(false);
                    }}
                    className="flex items-center gap-2 px-3 py-2 text-sm text-blue-600 hover:bg-blue-50 rounded-md w-full text-left"
                  >
                    <CheckCircle2 className="w-4 h-4" /> Finalizar Evento
                  </button>
                )}
              </>
            )}

            {/* ESTADO: FINALIZADO - Mostrar "Volver a Publicar" y "Archivar" */}
            {badgeStatus === 'finished' && (
              <>
                {onRepublish && (
                  <button
                    onClick={() => {
                      onRepublish(event.id, event.name);
                      setIsMenuOpen(false);
                    }}
                    className="flex items-center gap-2 px-3 py-2 text-sm text-purple-600 hover:bg-purple-50 rounded-md w-full text-left"
                  >
                    <RefreshCw className="w-4 h-4" /> Volver a Publicar
                  </button>
                )}
                {onArchive && (
                  <button
                    onClick={() => {
                      onArchive(event.id, event.name);
                      setIsMenuOpen(false);
                    }}
                    className="flex items-center gap-2 px-3 py-2 text-sm text-gray-600 hover:bg-gray-50 rounded-md w-full text-left"
                  >
                    <Archive className="w-4 h-4" /> Archivar Evento
                  </button>
                )}
              </>
            )}
            <button
              onClick={() => {
                onDelete(event.id, event.name);
                setIsMenuOpen(false);
              }}
              className="flex items-center gap-2 px-3 py-2 text-sm text-red-600 hover:bg-red-50 rounded-md w-full text-left"
            >
              <Trash2 className="w-4 h-4" /> Eliminar
            </button>
          </div>
        </Popover>
      </div>
    </div>
  );
});
